/**
 * Provides the `getValueByPath` utility function for retrieving the value of a nested property from an object or array using a string path, and its associated `GetValueByPathOptions` type.
 * @module getValueByPath
 */
// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';
/**
 * Retrieves the value of a nested property from an object or array using a string path.
 * Supports dot notation for object properties and bracket notation for array indices.
 *
 * **Behavior:**
 * - Takes an `obj` (object or array) and a `path` string (e.g., "prop.array[0].value").
 * - **Input validation:** Returns `undefined` immediately if `obj` or `path` is null, undefined, or empty (checked via `isEmpty`).
 * - **Path normalization:** Converts array bracket notation (`[index]`) to dot notation (`.index`) and splits the path into parts using the dot (`.`) as a delimiter. Filters out empty parts resulting from consecutive or leading/trailing dots. Returns `undefined` if the normalized path is empty.
 * - **Path traversal:** Iterates through the path parts:
 *  - If the current value (`current`) is not an object or array capable of being indexed further, traversal stops, and `undefined` is returned.
 *  - If a path part is a valid non-negative integer string *and* `current` is an array, it attempts to access the element at that index. Returns `undefined` if the index is out of bounds.
 *  - If a path part is not a valid array index *and* `current` is a non-null object, it attempts to access the property using the part as the key. Returns `undefined` if the property does not exist (using `hasOwnProperty`).
 *  - If `current` becomes `null` or `undefined` during traversal (before reaching the final part), `undefined` is returned.
 * - Returns the value found at the end of the specified path. The type is `unknown | undefined`, requiring the caller to perform type checks or assertions.
 *
 * @since 5.0 Initial implementation.
 * @param {Record<string, unknown> | unknown[] | null | undefined} obj - The object or array from which to retrieve the value. Can be null or undefined.
 * @param {string | null | undefined} path - The dot-and-bracket-notation path to the desired property (e.g., "user.addresses[0].city"). If null, undefined, or empty, the function returns `undefined`.
 * @return {unknown | undefined} The value found at the specified path, or `undefined` if the path is invalid, any intermediate part of the path does not exist, the input object/path is empty, or if the value at the end of the path is explicitly `undefined`.
 *
 * @example Importing the getValueByPath utility
 * ```typescript
 * import { getValueByPath } from '@automator/shared/utils/get-value-by-path';
 * ```
 *
 * @example Accessing nested object property
 * Retrieves the first name from a nested structure.
 * ```typescript
 * const data = { user: { name: { first: 'Alice' } } };
 * getValueByPath( data, 'user.name.first' );
 * // => 'Alice'
 * ```
 *
 * @example Accessing array element property
 * Retrieves the name of the first sibling from an array within an object.
 * ```typescript
 * const data = { siblings: [ { name: 'Bob' }, { name: 'Charlie' } ] };
 * getValueByPath( data, 'siblings[0].name' );
 * // => 'Bob'
 * ```
 *
 * @example Accessing array element directly
 * Retrieves the second element (index 1) from a root array.
 * ```typescript
 * const data = [ 'apple', 'banana', 'cherry' ];
 * getValueByPath( data, '[1]' );
 * // => 'banana'
 * ```
 *
 * @example Handling non-existent path
 * Returns undefined when a property or index in the path does not exist.
 * ```typescript
 * const data = { user: { name: 'Alice' } };
 * getValueByPath(data, 'user.address.city');
 * // => undefined
 * getValueByPath(data, 'user.name[0]'); // Accessing string as array
 * // => undefined
 * getValueByPath(['a', 'b'], '[2]'); // Index out of bounds
 * // => undefined
 * ```
 *
 * @example Handling invalid inputs
 * Returns undefined for null/undefined/empty object or path.
 * ```typescript
 * getValueByPath( null, 'a.b' );
 * // => undefined
 * getValueByPath( { a: 1 }, '' );
 * // => undefined
 * getValueByPath( { a: 1 }, null );
 * // => undefined
 * ```
 *
 * @example Handling path with only dots/brackets
 * Returns undefined if the normalized path becomes empty.
 * ```typescript
 * const data = { a: 1 };
 * getValueByPath( data, '.' );
 * // => undefined
 * getValueByPath( data, '..' );
 * // => undefined
 * getValueByPath( data, '[]' );
 * // => undefined
 * ```
 *
 * @remarks
 * - The return type is `unknown | undefined`. Callers should validate or assert the type of the returned value before using it in type-specific operations. Be careful with the type of the returned value.
 * - This function provides basic path traversal. It does not handle complex scenarios like function calls within paths.
 * - It uses `Object.prototype.hasOwnProperty.call` for safe property checking on objects.
 * - For comprehensive behavior details, including edge cases with different path formats, refer to the unit tests.
 */
export function getValueByPath(obj, path) {
    if (isEmpty(obj) || isEmpty(path)) {
        return;
    }
    // Normalize path by replacing `[index]` with `.index` for simpler splitting, then split by '.', and filter out empty strings that might result from leading/trailing dots or multiple dots together (e.g. path..like..this)
    const normalizedPath = path.replace(/\[(\d+)\]/g, '.$1');
    const pathParts = normalizedPath.split('.').filter(part => part.length > 0);
    if (pathParts.length === 0) {
        // This can happen if the original path was "[]" or "."
        return;
    }
    let current = obj;
    for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i];
        // Check if the current part is an array index (already normalized from [index] to index)
        const arrayIndex = parseInt(part, 10);
        if (!isNaN(arrayIndex)) {
            // It's a numeric string, try to access as an array index
            if (Array.isArray(current)) {
                if (arrayIndex >= 0 && arrayIndex < current.length) {
                    current = current[arrayIndex];
                }
                else {
                    // Index out of bounds
                    return;
                }
            }
            else {
                // Expected array for numeric index, but current is not an array
                return;
            }
        }
        else if (typeof current === 'object' && current !== null) {
            // It's a property name, access it
            // We check if current is an object and not null before trying to access a property
            if (Object.prototype.hasOwnProperty.call(current, part)) {
                current = current[part];
            }
            else {
                // Property does not exist
                return;
            }
        }
        else {
            // Path part not found or current is not an object/array that can be indexed further
            return;
        }
        // If current becomes null or undefined at any point (and it's not the last part), the path is invalid.
        if (!isDefined(current) && i < pathParts.length - 1) {
            return;
        }
    }
    return current;
}
;
